import React, {useContext} from "react";
import Box from "@material-ui/core/Box";
import Radio from "@material-ui/core/Radio";
import RadioGroupMui from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import clsx from "clsx";
import {Controller, UseFormReturn} from "react-hook-form";
import Form, {FormContext} from "../Form";
import useStyles from "./styles";

export type RadioItem = {
  label: React.ReactNode;
  value: string;
  content?: React.ReactNode;
  selected?: boolean;
  disabled?: boolean;
  children?: (parentSelected: boolean) => React.ReactNode;
};

export type RadioGroupProps<T extends Record<string, unknown>> = {
  items: RadioItem[];
  name: string;
  form?: UseFormReturn<T>;
  classes?: {
    root?: string;
    item?: string;
    labelRoot?: string;
    radioRoot?: string;
  };
};

export type RadioContentProps<T extends Record<string, unknown>> = {
  form: UseFormReturn<T>;
  component: React.ReactNode;
  contentStyle?: "radioContentFill" | "radioContentStart" | "radioContentFull";
};

export const RadioContent = <T extends Record<string, unknown>>({
  form,
  component,
  contentStyle,
}: RadioContentProps<T>) => {
  const classes = useStyles();
  return (
    <div className={classes.radioContent}>
      <Form
        className={
          (contentStyle && classes[contentStyle]) ?? classes.radioContentForm
        }
        onSubmit={() => null}
        form={form}
      >
        {component}
      </Form>
    </div>
  );
};
const RadioGroup = <T extends Record<string, unknown>>({
  items,
  form: propsForm,
  classes: classNames,
  name,
}: RadioGroupProps<T>) => {
  const classes = useStyles();
  const form = propsForm || (useContext(FormContext) as UseFormReturn<any>);

  const formLabelProps = (label: React.ReactNode) => ({
    classes: {
      root: classNames?.labelRoot,
      label: classes.label,
    },
    label,
  });

  const renderRadios = (selectedValue: string) =>
    items.map((item) => {
      const {value, label, content, disabled: itemDisabled, children} = item;
      const isSelected = selectedValue === value;

      return (
        <Box key={value} className={classNames?.item}>
          <Box>
            <FormControlLabel
              value={value}
              disabled={itemDisabled}
              control={
                <Radio
                  classes={{
                    root: clsx(classes.root, classNames?.radioRoot),
                    checked: classes.checked,
                  }}
                  color="primary"
                />
              }
              {...formLabelProps(label)}
            />
            {isSelected && content}
          </Box>
          {children && children(isSelected)}
        </Box>
      );
    });

  return (
    <Box display="flex" flexDirection="column" className={classNames?.root}>
      <Controller
        rules={{required: true}}
        control={form.control}
        name={name}
        render={({field}) => {
          return (
            <RadioGroupMui {...field}>
              {renderRadios(field.value)}
            </RadioGroupMui>
          );
        }}
      />
    </Box>
  );
};

export default RadioGroup;
